import React, { useRef, useState, useEffect } from "react";
import { Button, Checkbox, FormControlLabel } from "@material-ui/core"; // Add Checkbox and FormControlLabel
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { withRouter } from "react-router-dom";
import { useDispatch } from "react-redux";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { logoSign, loginLogo } from "../../../resources/images/index";
import { accountActions } from "../../../store/accounts/accountActions";
import Loader from "../../../components/common/Loader";
import curve from "../../../resources/images/curve.png";
import curveOverlay from "../../../resources/images/curveOverlay.png";
import WelcomeText from "./WelcomeText";
import useAesGcmEncrypt from "../../../utils/encryptionHook";
import { requestForToken } from "../../../FCMService";

// Function to generate a unique device key
const generateDeviceKey = () => {
  const deviceInfo = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    timestamp: Date.now(),
    random: Math.random().toString(36).substring(2, 15),
  };

  // Create a simple hash of device info
  const deviceKey = btoa(JSON.stringify(deviceInfo));
  return deviceKey;
};

// Function to get or create persistent device key
const getOrCreateDeviceKey = () => {
  let deviceKey = localStorage.getItem("deviceKey");
  if (!deviceKey) {
    deviceKey = generateDeviceKey();
    localStorage.setItem("deviceKey", deviceKey);
  }
  return deviceKey;
};

function Login(props) {
  const { encrypt } = useAesGcmEncrypt();

  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [fcmValue, setFcmValue] = useState(null);
  const [rememberMe, setRememberMe] = useState(false); // Add remember me state

  useEffect(() => {
    // Load saved email if remember me was checked
    const savedEmail = localStorage.getItem("rememberedEmail");
    const savedRememberMe = localStorage.getItem("rememberMe") === "true";

    if (savedRememberMe && savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    const getFCMToken = async () => {
      setLoading(true);
      const token = await requestForToken();
      setLoading(false);
      if (token) {
        setFcmValue(token);
      }
    };

    getFCMToken();
  }, []);

  useEffect(() => {
    const isAdmin = localStorage.getItem("isAdmin");
    if (isAdmin !== null) {
      if (isAdmin === "admin") {
        props.history.push({
          pathname: "/launches",
          state: { isLogin: true },
        });
      } else {
        props.history.push("/");
      }
    }
  }, [props.history]);

  const handleSubmit = async e => {
    setLoading(true);
    let encryptedPassword = await encrypt(password);

    // Get or create device key
    const deviceKey = getOrCreateDeviceKey();

    // Handle remember me functionality
    if (rememberMe) {
      localStorage.setItem("rememberedEmail", email);
      localStorage.setItem("rememberMe", "true");
    } else {
      localStorage.removeItem("rememberedEmail");
      localStorage.setItem("rememberMe", "false");
    }

    dispatch(
      accountActions.onSubmit(
        email,
        encryptedPassword.encryptedBase64,
        encryptedPassword.keyBase64,
        encryptedPassword.ivBase64,
        fcmValue,
        rememberMe, // Pass remember me value
        deviceKey, // Pass device key
      ),
    ).then(data => {
      if (data && data.data && data.data.success) {
        if (data.data.data.isTwoFactorEnabled) {
          props.history.push("/two-factor");
        } else {
          if (data.data.data.role === "admin") {
            props.history.push({
              pathname: "/dashboard",
              state: { isLogin: true },
            });
          } else if (
            ["customer", "company_user", "company_admin"].includes(
              data.data.data.role,
            )
          ) {
            props.history.push("/");
          }
        }
      } else {
        if (data && data.data) {
          toast.error(data.data.message);
        } else {
          toast.error("Password is not correct");
        }
      }

      setLoading(false);
    });
  };

  const loginForm = useRef("form");

  return (
    <div className='loggedinWrapper'>
      {isLoading ? <Loader /> : null}
      <div className='leftCol'>
        <WelcomeText />
        <span className='loginLeftshade' />
        <span className='loginRightShade'>
          <img src={curve} alt='' className='curve' />
          <img src={curveOverlay} alt='' className='curveOverlay' />
        </span>
      </div>
      <div className='rightCol'>
        <img src={loginLogo} alt='' />
        <img src={logoSign} alt='' style={{ marginLeft: "195px" }} />
        <span className='createAccount'>
          Sign In{" "}
          <i>
            or <Link to=''>Request Access</Link>
          </i>
        </span>

        <ValidatorForm ref={loginForm} onSubmit={handleSubmit}>
          <TextValidator
            id='email'
            label='Email or username'
            variant='outlined'
            name='email'
            fullWidth
            validators={["required", "isEmail"]}
            errorMessages={[
              "Please enter your email address",
              "Please enter a valid email address",
            ]}
            value={email}
            onChange={v => setEmail(v.target.value)}
          />

          <div className='gap' />
          <TextValidator
            id='password'
            label='Password'
            variant='outlined'
            name='password'
            fullWidth
            validators={["required"]}
            errorMessages={["Please enter your password"]}
            type={"password"}
            value={password}
            onChange={v => setPassword(v.target.value)}
          />

          {/* Add Remember Me checkbox */}
          <div
            style={{
              marginTop: "10px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
            }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}>
              <input
                type='checkbox'
                checked={rememberMe}
                onChange={e => setRememberMe(e.target.checked)}
                style={{
                  width: "16px",
                  height: "16px",
                  marginRight: "8px",
                  cursor: "pointer",
                  accentColor: "#3f51b5", // Material-UI primary color
                }}
              />
              <span
                style={{
                  fontSize: "14px",
                  color: "#fff",
                  userSelect: "none",
                  marginTop: "0px",
                }}>
                Remember Me
              </span>
            </label>
          </div>

          <Button type='submit' color='primary' variant='contained'>
            Login
          </Button>
        </ValidatorForm>

        <span className='forgotpssword'>
          <Link to='/forgot-password'>Forgot Password?</Link>
        </span>
      </div>
    </div>
  );
}

export default withRouter(Login);
